import React, { useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { TextFieldElement } from "react-hook-form-mui";

import { SmallCell, MediumCell, LargeCell } from "../style/tablecell";
import { TypographyNomalBold, TypographySmall } from '../style/typography';
import { BoxSpaceAroundGap10 } from '../style/box';
import { validationSchemaCheckReult } from '../func/validation_check';
import { CustomTextFieldElementCheckResult } from '../components/CustomTextFieldElement';

import { intensityMap, inspectionPriorityMap } from '../const/data_index';
import { normalizeIntensity, getIntensityPriority, handleUpdateCheckResultDam3 } from '../func/util';
import { DAM_REPORT_STANDARD_INTENSITY } from '../const/data_index';

const INITIAL_NOTE_TEMPLATE = "〇〇:〇〇 ITV:異常なし\n〇〇:〇〇 現場派遣出発";

export default function CheckResultDam3({ checkResult, setCheckResult, isReadOnly, facilityDams }) {
    checkResult.sort((a, b) => {
        // 第1優先：震度の降順
        const intensityA = intensityMap[a.intensity];
        const intensityB = intensityMap[b.intensity];
        const intensityDiff = intensityB - intensityA; // 降順
        if (intensityDiff !== 0) {
            return intensityDiff;
        }

        // 第2優先：観測地点の優先度の昇順
        const inspectionA = inspectionPriorityMap[a.inspections_name] || 0;
        const inspectionB = inspectionPriorityMap[b.inspections_name] || 0;
        return inspectionB - inspectionA; // 降順
    });
    console.log("Sorted Check Result:", checkResult);

    const { control, getValues, setValue, setError } = useForm({
        defaultValues: {
            checkResults: checkResult.map((row, index) => {
                if (!isReadOnly && !row.note) {
                    // 編集モードの場合のみテンプレートを設定
                    return { ...row, note: INITIAL_NOTE_TEMPLATE };
                }
                return row;
            })
        },
        resolver: yupResolver(validationSchemaCheckReult),
        mode: "onBlur"
    });

    useEffect(() => {
        // 編集モードかつ、テンプレート値が残っている場合にエラーを設定
        if (!isReadOnly) {
            const currentNote = getValues("checkResults.0.note");

            // noteがテンプレート値、または空の場合にエラーを設定
            if (currentNote === INITIAL_NOTE_TEMPLATE || !currentNote) {
                setError("checkResults.0.note", {
                    type: "manual",
                    message: "備考がテンプレートのままです。修正してください。",
                });
            }
        }
    }, [isReadOnly, setError, getValues]);

    const handleChange = (recordIndex, field, value) => {
        const prevCheckResult = getValues("checkResults") || [];

        let updatedValue = value;

        if (field === "note") {
            const updatedValue = value ?? null;

            // 1. 親の状態 (setCheckResult) を更新
            // facilityDams に含まれるダムのレコードのみ note を更新
            const targetFacilityUuid = checkResult[0].facility_uuid;

            setCheckResult(prevAllCheckResults => {
                return prevAllCheckResults.map(row =>
                    row.facility_uuid === targetFacilityUuid && facilityDams.includes(row.facility_uuid)
                        ? { ...row, [field]: updatedValue }
                        : row
                );
            });

            // 2. useFormの内部状態を全レコード更新
            const prevCheckResult = getValues("checkResults") || [];
            prevCheckResult.forEach((row, idx) => {
                if (row.facility_uuid === targetFacilityUuid && facilityDams.includes(row.facility_uuid)) {
                    setValue(`checkResults.${idx}.${field}`, updatedValue);
                }
            });

            return;
        }

        if (field === "intensity") {
            updatedValue = normalizeIntensity(value);
        }
        const updatedRow = {
            ...prevCheckResult[recordIndex],
            [field]: updatedValue ?? false
        };

        // 親の状態更新関数を呼び出す
        handleUpdateCheckResultDam3(setCheckResult, updatedRow);

        // useFormの内部状態を更新
        setValue(`checkResults.${recordIndex}.${field}`, updatedValue ?? false);
    };

    const renderAccelerationField = (row, actualIndex, fieldName, label) => (
        isReadOnly ? (
            row[fieldName] || "—"
        ) : (
            <TextFieldElement
                control={control}
                name={`checkResults.${actualIndex}.${fieldName}`}
                value={row[fieldName] || ""}
                label={label}
                fullWidth
                onChange={(e) => handleChange(row.facility_uuid, fieldName, e.target.value)}
                disabled={true}
                FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
            />
        )
    );

    const renderTextFieldElement = (row, index, field, label, row_length) => (
        isReadOnly ? (
            row[field] || "—"
        ) : (
            <CustomTextFieldElementCheckResult
                name={field}
                index={index}
                control={control}
                label={label}
                onChange={(e) => handleChange(index, field, e.target.value)}
                row={row_length}
            />
        )
    );

    return (
        <>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <SmallCell><TypographyNomalBold>No</TypographyNomalBold></SmallCell>
                            <SmallCell><TypographyNomalBold>ダム</TypographyNomalBold></SmallCell>
                            <SmallCell><TypographyNomalBold>震度</TypographyNomalBold></SmallCell>
                            <LargeCell><TypographyNomalBold>気象台発表震度観測点</TypographyNomalBold></LargeCell>
                            <SmallCell>
                                <TypographyNomalBold>地震計の有無</TypographyNomalBold>
                                <TypographySmall>※地震計を設置した場合はシステム担当者に連絡してください</TypographySmall>
                            </SmallCell>
                            <MediumCell><TypographyNomalBold>加速度：天端(X/Y/Z)</TypographyNomalBold></MediumCell>
                            <MediumCell><TypographyNomalBold>加速度：基礎(X/Y/Z)</TypographyNomalBold></MediumCell>
                            <LargeCell><TypographyNomalBold>備考</TypographyNomalBold></LargeCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {checkResult
                            .filter((_, index) => {
                                // isReadOnly が true の場合は、ソート済みの先頭1件のみ表示
                                if (isReadOnly) {
                                    return index === 0;
                                }
                                // 編集モード（isReadOnly = false）の場合は全件表示
                                return true;
                            })
                            .map((row, index, filteredArray) => {
                                const actualIndex = checkResult.findIndex(r => r === row);
                                const cellNoBorderSx = { borderBottom: 'none' };

                                // 表示されている件数に応じて rowspan を調整
                                const displayRowCount = filteredArray.length;

                                return (
                                    <TableRow key={`${row.earthquake_intensity_uuid}-${index}`}>
                                        <SmallCell sx={cellNoBorderSx}>{index + 1}</SmallCell>

                                        {/* ダム名 */}
                                        {index === 0 && (
                                            <SmallCell rowSpan={displayRowCount} sx={cellNoBorderSx}>
                                                {row.facility_name}
                                            </SmallCell>
                                        )}

                                        {/* 震度 */}
                                        <SmallCell sx={cellNoBorderSx}>
                                            {isReadOnly ? (
                                                row.intensity || "—"
                                            ) : (
                                                <CustomTextFieldElementCheckResult
                                                    name="intensity"
                                                    index={actualIndex}
                                                    control={control}
                                                    label="震度"
                                                    onChange={(e) => handleChange(actualIndex, "intensity", e.target.value)}
                                                />
                                            )}
                                        </SmallCell>

                                        {/* 気象台発表震度観測点 */}
                                        <LargeCell sx={cellNoBorderSx}>
                                            {isReadOnly ? (
                                                row.inspections_name || "—"
                                            ) : (
                                                <CustomTextFieldElementCheckResult
                                                    name="inspections_name"
                                                    index={actualIndex}
                                                    control={control}
                                                    label="気象台発表震度観測点"
                                                    onChange={(e) => handleChange(actualIndex, "inspections_name", e.target.value)}
                                                />
                                            )}
                                        </LargeCell>

                                        {/* 地震計の有無 */}
                                        {index === 0 && (
                                            <SmallCell rowSpan={checkResult.length} sx={cellNoBorderSx}>無</SmallCell>
                                        )}

                                        {/* 天端加速度 */}
                                        {index === 0 && (
                                            <TableCell
                                                rowSpan={checkResult.length}
                                                sx={cellNoBorderSx}
                                            >
                                                <BoxSpaceAroundGap10>
                                                    {renderAccelerationField(row, actualIndex, "gal_top_x", "X")}
                                                    {renderAccelerationField(row, actualIndex, "gal_top_y", "Y")}
                                                    {renderAccelerationField(row, actualIndex, "gal_top_z", "Z")}
                                                </BoxSpaceAroundGap10>
                                            </TableCell>
                                        )}

                                        {/* 基礎加速度 */}
                                        {index === 0 && (
                                            <TableCell
                                                rowSpan={checkResult.length}
                                                sx={cellNoBorderSx}
                                            >
                                                <BoxSpaceAroundGap10>
                                                    {renderAccelerationField(row, actualIndex, "gal_base_x", "X")}
                                                    {renderAccelerationField(row, actualIndex, "gal_base_y", "Y")}
                                                    {renderAccelerationField(row, actualIndex, "gal_base_z", "Z")}
                                                </BoxSpaceAroundGap10>
                                            </TableCell>
                                        )}

                                        {/* 備考 */}
                                        {index === 0 && (
                                            <TableCell
                                                rowSpan={checkResult.length}
                                                sx={cellNoBorderSx}
                                            >
                                                {/* 備考は checkResult[0].note  */}
                                                {renderTextFieldElement(checkResult[0], 0, "note", "備考", checkResult.length)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}